import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';

import { getUnits } from 'actions/landlordPropertiesActions';
import { updatePurchaseOrder, getPurchaseOrder } from 'actions/landlordPurchaseOrdersActions';
import { postProject } from 'components/landlord_app/maintenance/projects/store/actions';
import CtaPrimary from 'components/ui/CtaPrimary';
import DrawerFooter from 'components/ui/DrawerFooter';
import DrawerHeader from 'components/ui/DrawerHeader';
import DrawerBody from 'components/ui/DrawerBody';
import Row from 'components/ui/Row';

import { Formik, Form } from 'formik';
import { PurchaseOrderSchema } from './helpers/purchaseOrderSchema';
import styled from 'styled-components';

import { PurchaseOrderForm } from './PurchaseOrderForm';
import NewPurchaseOrderCatalogItem from './NewPurchaseOrderCatalogItem';
import { CreateNewPurchaseOrderCatalogItemForm } from './CreateNewPurchaseOrderCatalogItemForm';
import { PurchaseOrderItemList } from './PurchaseOrderItemList';
import { InternalComments } from './InternalComments';
import { LinkServiceRequests } from './LinkServiceRequests';
import { LinkProject } from './LinkProject';
import { formatMoney } from 'helpers/Formatter';
import { getTranslatedString } from 'helpers/i18n';
import { toMomentDate } from 'helpers/Formatter';
import Lottie from 'react-lottie';

import errorAnimationData from 'assets/error.json';
import loadingAnimationData from 'assets/loading.json';
import successAnimationData from 'assets/success.json';

import DrawerSuccessMessages from 'components/ui/DrawerSuccessMessages';

const StyledRowBottomMargin = styled(Row)`
  margin-bottom: 32px;
`;

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space;
  height: 100%;
  justify-content: space-between;
`;

export const PURCHASE_ORDER_FORM = 'purchase_order_form';
export const PURCHASE_ORDER_CATALOG_ITEM_FORM = 'purchase_order_catalog_item_form';
export const NEW_PURCHASE_ORDER_CATALOG_ITEM_FORM = 'new_purchase_order_catalog_item_form';

export const EditRequesterPurchaseOrderDrawer = connect(
  store => ({
    properties: store.landlordProperties.properties,
    units: store.landlordProperties.units,
    vendors: store.landlordPurchaseOrders.vendors,
  }),
  {
    getUnits,
    updatePurchaseOrder,
    getPurchaseOrder,
    postProject,
  }
)(
  ({
    purchaseOrder,
    toggleEditPurchaseOrder,
    properties,
    units,
    getUnits,
    vendors,
    updatePurchaseOrder,
    fetchPurchaseOrders,
    getPurchaseOrderStats,
    getPurchaseOrder,
    postProject,
  }) => {
    const [purchaseOrderItems, setPurchaseOrderItems] = useState([]);
    const [purchaseOrderFormStep, setPurchaseOrderFormStep] = useState(PURCHASE_ORDER_FORM);
    const [modifyItem, setModifyItem] = useState(false);
    const [itemChangeIndex, setItemChangeIndex] = useState('');
    const [toggleSuccess, setToggleSuccess] = useState(false);
    const [toggleError, setToggleError] = useState(false);
    const [isUpdatingPurchaseOrder, setIsUpdatingPurchaseOrder] = useState(false);
    const [vendorHasCatalogItems, setVendorHasCatalogItems] = useState(false);
    const [serviceRequests, setServiceRequests] = useState(purchaseOrder.maintenance_tickets);
    const [selectedProject, setSelectedProject] = useState(purchaseOrder.project);

    useEffect(
      () => {
        if (purchaseOrder.purchase_order_items) {
          let cleanPurchaseOrderItems = [...purchaseOrder.purchase_order_items];
          cleanPurchaseOrderItems.forEach(item => (item.state = 'no_change'));
          setPurchaseOrderItems(cleanPurchaseOrderItems);
        }
        if (purchaseOrder.external_vendor_id && purchaseOrder.vendor_name) {
          let vendor = vendors.find(
            vendor => vendor.id === parseInt(purchaseOrder.external_vendor_id)
          );
          let uniqueCatalogCategories = vendor.catalog_items.reduce(
            (acc, x) =>
              acc.concat(acc.find(y => y.item_category_id === x.item_category_id) ? [] : [x]),
            []
          );
          setVendorHasCatalogItems(uniqueCatalogCategories.length > 0);
        }
      },
      [
        purchaseOrder.purchase_order_items,
        setPurchaseOrderItems,
        purchaseOrder.external_vendor_id,
        purchaseOrder.vendor_name,
        vendors,
      ]
    );

    let serviceRequestIds = purchaseOrder.maintenance_tickets.map(maintenance_ticket =>
      String(maintenance_ticket.id)
    );
    return (
      <Formik
        initialValues={{
          vendor_id: parseInt(purchaseOrder.external_vendor_id) || '',
          vendor_name: purchaseOrder.vendor_name || '',
          required_date: toMomentDate(purchaseOrder.required_date),
          property_id: purchaseOrder.property_id || '',
          project_id: purchaseOrder.project_id || '',
          purchase_order_items: purchaseOrder.purchase_order_items || [],
          service_requests: serviceRequestIds || [],
          internal_comments: '',
          description: purchaseOrder.description || '',
          title: purchaseOrder.title || '',
          purchase_order_item: {
            item_name: '',
            item_number: '',
            estimated_cost: '',
            total_cost: '',
            item_description: '',
            quantity: '',
            unit_id: undefined,
            unit_number: undefined,
            item_category_id: '',
          },
          new_purchase_order_item: {
            item_name: '',
            item_number: '',
            estimated_cost: '',
            total_cost: '',
            item_description: '',
            quantity: '',
            unit_id: undefined,
            unit_number: undefined,
            item_category_id: '',
          },
          catalog_form_errors: {
            item_category_id: false,
            item_name: false,
            quantity: false,
          },
          new_catalog_form_errors: {
            item_name: false,
            item_description: false,
            quantity: false,
            estimated_cost: false,
          },
        }}
        validationSchema={PurchaseOrderSchema}
        onSubmit={values => {
          setIsUpdatingPurchaseOrder(true);
          updatePurchaseOrder(values, purchaseOrder.id)
            .then(res => {
              setToggleSuccess(true);
              getPurchaseOrder(purchaseOrder.id);
              getPurchaseOrderStats();
              fetchPurchaseOrders({
                updateList: true,
              });
              setIsUpdatingPurchaseOrder(false);
            })
            .catch(error => {
              setIsUpdatingPurchaseOrder(false);
              setToggleError(true);
              console.error(error);
            });
        }}
      >
        {({ values, setFieldValue, submitForm, errors, touched, resetForm }) => {
          const cleanUpFormOnClose = () => {
            resetForm();
            toggleEditPurchaseOrder(false);
            setPurchaseOrderFormStep(PURCHASE_ORDER_FORM);
            setToggleSuccess(false);
            setPurchaseOrderItems(purchaseOrder.purchase_order_items);
            let vendor = vendors.find(
              vendor => vendor.id === parseInt(purchaseOrder.external_vendor_id)
            );
            let uniqueCatalogCategories = vendor.catalog_items.reduce(
              (acc, x) =>
                acc.concat(acc.find(y => y.item_category_id === x.item_category_id) ? [] : [x]),
              []
            );
            setVendorHasCatalogItems(uniqueCatalogCategories.length > 0);
          };
          const handlePropertyChange = value => {
            setFieldValue('property_id', value);
            getUnits(value, { for_multiselect: true });
          };

          const handleBackClick = () => {
            if (modifyItem) {
              resetCatalogFormErrors();
              resetNewCatalogFormErrors();
              resetPurchaseOrderItem();
              resetNewPurchaseOrderItem();
              setPurchaseOrderFormStep(PURCHASE_ORDER_FORM);
              return;
            }
            if (purchaseOrderFormStep === PURCHASE_ORDER_CATALOG_ITEM_FORM) {
              resetCatalogFormErrors();
              resetPurchaseOrderItem();
              setPurchaseOrderFormStep(PURCHASE_ORDER_FORM);
              return;
            }
            if (purchaseOrderFormStep === NEW_PURCHASE_ORDER_CATALOG_ITEM_FORM) {
              resetNewCatalogFormErrors();
              resetNewPurchaseOrderItem();
              if (vendorHasCatalogItems) {
                setPurchaseOrderFormStep(PURCHASE_ORDER_CATALOG_ITEM_FORM);
              } else {
                setPurchaseOrderFormStep(PURCHASE_ORDER_FORM);
              }
              return;
            }
          };

          const resetPurchaseOrderItemList = () => {
            setFieldValue('purchase_order_items', []);
            setPurchaseOrderItems([]);
          };

          const resetPurchaseOrderItem = () => {
            setFieldValue('purchase_order_item', {
              item_name: '',
              item_number: '',
              estimated_cost: '',
              item_description: '',
              quantity: '',
              unit_id: '',
              unit_number: '',
              item_category_id: '',
            });
          };

          const resetNewPurchaseOrderItem = () => {
            setFieldValue('new_purchase_order_item', {
              item_name: '',
              item_number: '',
              estimated_cost: '',
              item_description: '',
              quantity: '',
              unit_id: '',
              unit_number: '',
              item_category_id: '',
            });
          };

          const checkCatalogFormErrors = (purchaseOrderItem, errorState) => {
            let formErrors = errorState;

            if (!purchaseOrderItem.item_category_id) {
              formErrors.item_category_id = true;
            }
            if (!purchaseOrderItem.item_name) {
              formErrors.item_name = true;
            }
            if (!purchaseOrderItem.quantity) {
              formErrors.quantity = true;
            }
            setFieldValue('catalog_form_errors', formErrors);
          };

          const resetCatalogFormErrors = () => {
            setFieldValue('catalog_form_errors', {
              item_category_id: false,
              item_name: false,
              quantity: false,
            });
          };

          const checkNewCatalogFormErrors = (purchaseOrderItem, errorState) => {
            let formErrors = errorState;

            if (!purchaseOrderItem.item_name) {
              formErrors.item_name = true;
            }
            if (!purchaseOrderItem.item_description) {
              formErrors.item_description = true;
            }
            if (!purchaseOrderItem.quantity) {
              formErrors.quantity = true;
            }
            if (!purchaseOrderItem.estimated_cost) {
              formErrors.estimated_cost = true;
            }
            setFieldValue('new_catalog_form_errors', formErrors);
          };

          const resetNewCatalogFormErrors = () => {
            setFieldValue('new_catalog_form_errors', {
              item_name: false,
              item_description: false,
              quantity: false,
              estimated_cost: false,
            });
          };

          const addPurchaseOrderItemToList = values => {
            if (
              !values.purchase_order_item.item_category_id ||
              !values.purchase_order_item.item_name ||
              !values.purchase_order_item.quantity
            ) {
              checkCatalogFormErrors(values.purchase_order_item, values.catalog_form_errors);
              return;
            }
            let newPurchaseOrderItems = [...values.purchase_order_items];
            values.purchase_order_item.state = 'newly_added';
            newPurchaseOrderItems.push(values.purchase_order_item);
            setFieldValue('purchase_order_items', newPurchaseOrderItems);
            setPurchaseOrderItems(newPurchaseOrderItems);
            resetPurchaseOrderItem();
            resetNewPurchaseOrderItem();
            resetCatalogFormErrors();
            setPurchaseOrderFormStep(PURCHASE_ORDER_FORM);
          };

          const addNewPurchaseOrderCatalogItemToList = values => {
            if (
              !values.new_purchase_order_item.item_name ||
              !values.new_purchase_order_item.item_description ||
              !values.new_purchase_order_item.quantity ||
              !values.new_purchase_order_item.estimated_cost
            ) {
              checkNewCatalogFormErrors(
                values.new_purchase_order_item,
                values.new_catalog_form_errors
              );
              return;
            }
            let newPurchaseOrderItems = [...values.purchase_order_items];
            values.new_purchase_order_item.state = 'newly_added';
            newPurchaseOrderItems.push(values.new_purchase_order_item);
            setFieldValue('purchase_order_items', newPurchaseOrderItems);
            setPurchaseOrderItems(newPurchaseOrderItems);
            resetPurchaseOrderItem();
            resetNewPurchaseOrderItem();
            resetCatalogFormErrors();
            setPurchaseOrderFormStep(PURCHASE_ORDER_FORM);
          };

          const removePurchaseOrderItem = index => {
            let newPurchaseOrderItems = [...purchaseOrderItems];
            newPurchaseOrderItems.splice(index, 1);
            setPurchaseOrderItems(newPurchaseOrderItems);
            setFieldValue('purchase_order_items', newPurchaseOrderItems);
          };

          const editPurchaseOrderCatalogItem = index => {
            let purchaseOrderItem = purchaseOrderItems[index];
            let form_step = NEW_PURCHASE_ORDER_CATALOG_ITEM_FORM;

            setFieldValue('new_purchase_order_item', {
              item_name: purchaseOrderItem.item_name,
              item_number: purchaseOrderItem.item_number,
              estimated_cost: purchaseOrderItem.estimated_cost,
              item_description: purchaseOrderItem.item_description,
              quantity: purchaseOrderItem.quantity,
              unit_id: purchaseOrderItem.unit_id,
              unit_number: purchaseOrderItem.unit_number,
              item_category_id: purchaseOrderItem.item_category_id,
              state: purchaseOrderItem.state,
            });

            if (purchaseOrderItem.item_category_id) {
              form_step = PURCHASE_ORDER_CATALOG_ITEM_FORM;
              setFieldValue('purchase_order_item', {
                item_name: purchaseOrderItem.item_name,
                item_number: purchaseOrderItem.item_number,
                estimated_cost: purchaseOrderItem.estimated_cost,
                item_description: purchaseOrderItem.item_description,
                quantity: purchaseOrderItem.quantity,
                unit_id: purchaseOrderItem.unit_id,
                unit_number: purchaseOrderItem.unit_number,
                item_category_id: purchaseOrderItem.item_category_id,
                state: purchaseOrderItem.state,
              });
            }
            setModifyItem(true);
            setItemChangeIndex(index);
            setPurchaseOrderFormStep(form_step);
          };

          const savePurchaseOrderItemChange = values => {
            let newPurchaseOrderItems = [...purchaseOrderItems];
            newPurchaseOrderItems[itemChangeIndex] = values.new_purchase_order_item;
            if (purchaseOrderFormStep === PURCHASE_ORDER_CATALOG_ITEM_FORM) {
              newPurchaseOrderItems[itemChangeIndex] = values.purchase_order_item;
            }
            if (newPurchaseOrderItems[itemChangeIndex].state !== 'newly_added') {
              newPurchaseOrderItems[itemChangeIndex].state = 'modified';
            }
            setFieldValue('purchase_order_items', newPurchaseOrderItems);
            setPurchaseOrderItems(newPurchaseOrderItems);
            resetPurchaseOrderItem();
            resetNewPurchaseOrderItem();
            resetCatalogFormErrors();
            resetNewCatalogFormErrors();
            setPurchaseOrderFormStep(PURCHASE_ORDER_FORM);
            setModifyItem(false);
          };

          const getSelectedVendor = vendorHmy => {
            return vendors.find(v => v.id === vendorHmy);
          };

          const handleCommentChange = event => {
            setFieldValue('internal_comments', event.currentTarget.value);
          };

          const handleSelectServiceRequest = serviceRequest => {
            let serviceRequestIds = values.service_requests;
            if (serviceRequestIds.includes(String(serviceRequest.id)) === false) {
              serviceRequestIds.push(serviceRequest.id);
            }
            setFieldValue('service_requests', serviceRequestIds);

            let serviceRequestsArray = serviceRequests;
            serviceRequestsArray.push(serviceRequest.attributes);
            setServiceRequests(serviceRequestsArray);
          };

          const handleUnlinkServiceRequest = serviceRequestId => {
            let serviceRequestIds = values.service_requests;

            serviceRequestIds = serviceRequestIds.filter(
              serviceRequest => serviceRequest !== String(serviceRequestId)
            );

            setFieldValue('service_requests', serviceRequestIds);

            let serviceRequestsArray = serviceRequests;
            serviceRequestsArray = serviceRequestsArray.filter(
              serviceRequest => serviceRequest.id !== serviceRequestId
            );
            setServiceRequests(serviceRequestsArray);
          };

          const handleSelectProject = (projectId, project) => {
            setFieldValue('project_id', projectId);
            setSelectedProject(project);
          };

          const handleUnlinkProject = _projectId => {
            setFieldValue('project_id', '');
            setSelectedProject(null);
          };

          const toggleSuccessDrawer = () => {
            setToggleSuccess(false);
            cleanUpFormOnClose();
          };

          const resetError = () => {
            setToggleError(false);
          };

          if (isUpdatingPurchaseOrder) {
            return renderLoading();
          }

          if (toggleSuccess) {
            return renderSuccess(toggleSuccessDrawer);
          }

          if (toggleError) {
            return renderError(resetError);
          }

          return (
            <StyledForm>
              <React.Fragment>
                <RenderDrawerHeader
                  purchaseOrderFormStep={purchaseOrderFormStep}
                  backAction={handleBackClick}
                  toggleEditPurchaseOrder={toggleEditPurchaseOrder}
                  resetForm={resetForm}
                  cleanUpFormOnClose={cleanUpFormOnClose}
                />

                <DrawerBody>
                  {purchaseOrderFormStep === PURCHASE_ORDER_FORM && (
                    <React.Fragment>
                      <div>
                        <StyledRowBottomMargin gutter={15}>
                          <PurchaseOrderForm
                            values={values}
                            setFieldValue={setFieldValue}
                            properties={properties}
                            vendors={vendors}
                            setPurchaseOrderFormStep={setPurchaseOrderFormStep}
                            handlePropertyChange={handlePropertyChange}
                            errors={errors}
                            touched={touched}
                            resetPurchaseOrderItemList={resetPurchaseOrderItemList}
                            vendorHasCatalogItems={vendorHasCatalogItems}
                            setVendorHasCatalogItems={setVendorHasCatalogItems}
                          />
                        </StyledRowBottomMargin>
                        {purchaseOrderItems.length > 0 && (
                          <PurchaseOrderItemList
                            data={purchaseOrderItems}
                            removePurchaseOrderItem={removePurchaseOrderItem}
                            setPurchaseOrderFormStep={setPurchaseOrderFormStep}
                            editPurchaseOrderCatalogItem={editPurchaseOrderCatalogItem}
                            vendorHasCatalogItems={vendorHasCatalogItems}
                          />
                        )}
                      </div>
                      <Row gutter={15}>
                        <LinkServiceRequests
                          handleSelectServiceRequest={handleSelectServiceRequest}
                          handleUnlinkServiceRequest={handleUnlinkServiceRequest}
                          propertyId={values.property_id}
                          selectedServiceRequests={values.service_requests}
                          serviceRequests={serviceRequests}
                        />
                      </Row>
                      <Row gutter={15}>
                        <LinkProject
                          handleSelectProject={handleSelectProject}
                          handleUnlinkProject={handleUnlinkProject}
                          selectedProject={selectedProject}
                          propertyId={values.property_id}
                          properties={properties}
                          units={units}
                          postProject={postProject}
                          fetchUnitsOfProperty={handlePropertyChange}
                        />
                      </Row>
                      <Row gutter={15}>
                        <InternalComments
                          handleCommentChange={handleCommentChange}
                          values={values}
                          comments={purchaseOrder.internal_comments}
                          noBackground
                        />
                      </Row>
                    </React.Fragment>
                  )}
                  {purchaseOrderFormStep === PURCHASE_ORDER_CATALOG_ITEM_FORM && (
                    <NewPurchaseOrderCatalogItem
                      setFieldValue={setFieldValue}
                      vendor={getSelectedVendor(values.vendor_id)}
                      purchase_order_item={values.purchase_order_item}
                      units={units}
                      setPurchaseOrderFormStep={setPurchaseOrderFormStep}
                      formErrors={values.catalog_form_errors}
                    />
                  )}
                  {purchaseOrderFormStep === NEW_PURCHASE_ORDER_CATALOG_ITEM_FORM && (
                    <CreateNewPurchaseOrderCatalogItemForm
                      setFieldValue={setFieldValue}
                      new_purchase_order_item={values.new_purchase_order_item}
                      units={units}
                      formErrors={values.new_catalog_form_errors}
                    />
                  )}
                </DrawerBody>

                <RenderDrawerFooter
                  purchaseOrderFormStep={purchaseOrderFormStep}
                  disableFooterAction={false}
                  values={values}
                  addPurchaseOrderItemToList={addPurchaseOrderItemToList}
                  addNewPurchaseOrderCatalogItemToList={addNewPurchaseOrderCatalogItemToList}
                  submitForm={submitForm}
                  savePurchaseOrderItemChange={savePurchaseOrderItemChange}
                  modifyItem={modifyItem}
                  isUpdatingPurchaseOrder={isUpdatingPurchaseOrder}
                />
              </React.Fragment>
            </StyledForm>
          );
        }}
      </Formik>
    );
  }
);

const RenderDrawerHeader = ({ purchaseOrderFormStep, backAction, cleanUpFormOnClose }) => {
  let backOnClick = null;
  let headerTitle = getTranslatedString(
    'landlordPortal.editRequesterPurchaseOrderDrawer.modifyPurchaseOrder'
  );
  let subHeader = getTranslatedString('landlordPortal.purchaseOrderForm.indicates');

  if (purchaseOrderFormStep === PURCHASE_ORDER_CATALOG_ITEM_FORM) {
    headerTitle = getTranslatedString('landlordPortal.addPurchaseOrderDrawer.addACatalogItem');
    backOnClick = backAction;
  }
  if (purchaseOrderFormStep === NEW_PURCHASE_ORDER_CATALOG_ITEM_FORM) {
    headerTitle = getTranslatedString('landlordPortal.addPurchaseOrderDrawer.addANewCatalogItem');
    backOnClick = backAction;
  }
  return (
    <DrawerHeader
      subHeader={subHeader}
      onBack={backOnClick}
      onClose={cleanUpFormOnClose}
      title={headerTitle}
    />
  );
};

const RenderDrawerFooter = ({
  purchaseOrderFormStep,
  values,
  addPurchaseOrderItemToList,
  addNewPurchaseOrderCatalogItemToList,
  submitForm,
  savePurchaseOrderItemChange,
  modifyItem,
  isUpdatingPurchaseOrder,
}) => {
  let estimatedTotal = 0;
  if (values.purchase_order_items.length > 0) {
    estimatedTotal = values.purchase_order_items
      .map(item => item.total_cost)
      .reduce((acc, curr) => acc + curr);
  }

  let contentKey = 'landlordPortal.editRequesterPurchaseOrderDrawer.saveChanges'; // default purchase order form step is 'purchase_order_form'
  let handleClick = () => submitForm();
  let footerMessage = `${formatMoney(estimatedTotal)}${' '}${getTranslatedString(
    'landlordPortal.addPurchaseOrderDrawer.totalEstimatedCost'
  )}`;
  if (purchaseOrderFormStep === PURCHASE_ORDER_CATALOG_ITEM_FORM) {
    contentKey = 'landlordPortal.addPurchaseOrderDrawer.addCatalogItem';
    handleClick = () => addPurchaseOrderItemToList(values);
    footerMessage = getTranslatedString(
      'landlordPortal.addPurchaseOrderDrawer.pleaseEnterAllDetails'
    );
    if (modifyItem) {
      contentKey = 'landlordPortal.addPurchaseOrderDrawer.saveChanges';
      handleClick = () => savePurchaseOrderItemChange(values);
    }
  }
  if (purchaseOrderFormStep === NEW_PURCHASE_ORDER_CATALOG_ITEM_FORM) {
    contentKey = 'landlordPortal.addPurchaseOrderDrawer.addNewCatalogItem';
    handleClick = () => addNewPurchaseOrderCatalogItemToList(values);
    footerMessage = getTranslatedString(
      'landlordPortal.addPurchaseOrderDrawer.pleaseEnterAllDetails'
    );
    if (modifyItem) {
      contentKey = 'landlordPortal.addPurchaseOrderDrawer.saveChanges';
      handleClick = () => savePurchaseOrderItemChange(values);
    }
  }

  return (
    <DrawerFooter
      primaryActionText={getTranslatedString(contentKey)}
      onPrimaryActionClick={handleClick}
      secondaryText={footerMessage}
    />
  );
};

const renderLoading = () => {
  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: loadingAnimationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  return (
    <DrawerSuccessMessages
      headerImageSource={<Lottie options={defaultOptions} height={150} width={150} />}
      headerText={getTranslatedString('landlordPortal.addPurchaseOrderDrawer.loadingHeader')}
      bodyText={getTranslatedString('globalDrawer.loadingBody')}
    />
  );
};

const renderError = resetError => {
  const defaultOptions = {
    loop: false,
    autoplay: true,
    animationData: errorAnimationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  return (
    <DrawerSuccessMessages
      headerImageSource={<Lottie options={defaultOptions} height={150} width={150} />}
      headerText={getTranslatedString('landlordPortal.addPurchaseOrderDrawer.errorHeader')}
      bodyText={getTranslatedString('globalDrawer.errorBody')}
      cta={
        <CtaPrimary onClick={resetError}>{getTranslatedString('globalDrawer.goBack')}</CtaPrimary>
      }
    />
  );
};

const renderSuccess = handleClose => {
  const defaultOptions = {
    loop: false,
    autoplay: true,
    animationData: successAnimationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  return (
    <DrawerSuccessMessages
      headerImageSource={<Lottie options={defaultOptions} height={150} width={150} />}
      headerText={getTranslatedString('landlordPortal.addPurchaseOrderDrawer.successHeader')}
      bodyText={getTranslatedString('globalDrawer.successBody')}
      cta={<CtaPrimary onClick={handleClose}>{getTranslatedString('close')}</CtaPrimary>}
    />
  );
};
